/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */

import React from 'react';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';

import { SCHEMA } from '../../../constants';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import UserTag from '../../theme/UserTag';
import FormWrapper from '../../theme/FormWrapper';
import FormElement from '../../theme/FormElement';
import FormActions from '../../theme/FormActions';
import FormFooter from '../../theme/FormFooter';
import ErrorBanner from '../../theme/ErrorBanner';
import TnC from '../../theme/TnC';
import { ConsentCheckbox, GroupConsent } from '../../theme/GroupConsent';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import InputValidator from '../../../services/input-validator';
import Logger from '../../../services/logger';

const gaScreenName = 'SignUp - Email';

const FormContent = (props) => {
  const { trackPwl } = useAuth0();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
  } = props;

  const [isInitiated, setIsInitiated] = React.useState(false);

  const { email, name, mobile, password, marketingConsentPreference } = values;

  const { errors: formErrors = {} } = errors;
  const { password: passwordErrors = '' } = formErrors;

  const isPasswordValidFor = (criteria) =>
    (password || touched.password) && !passwordErrors.includes(criteria);
  const { minLengthMessage, minOneNumberMessage, minOneLetterMessage } = SCHEMA.password;
  const { minLengthText, minOneNumberText, minOneLetterText } = SCHEMA.passwordMessage;
  const passWordHelpText = {
    title: 'Password must have:',
    validations: [
      {
        isValid: isPasswordValidFor(minLengthMessage),
        message: minLengthText,
      },
      {
        isValid: isPasswordValidFor(minOneNumberMessage),
        message: minOneNumberText,
      },
      {
        isValid: isPasswordValidFor(minOneLetterMessage),
        message: minOneLetterText,
      },
    ],
  };

  const showErrorFor = (fieldName) => touched[fieldName] && Boolean(formErrors[fieldName]);

  const showNameError = showErrorFor('name');
  const showMobileError = showErrorFor('mobile');
  const showPasswordError = showErrorFor('password');

  const isSubmitButtonDisabled = !dirty || !isValid;

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled],
  );

  const handleFocus = React.useCallback(() => {
    if (!isInitiated) {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Fill In',
        event_label: 'SignUp_Email_Initiated',
        origin_target: 'name',
      });

      setIsInitiated(true);
    }
  }, [isInitiated, setIsInitiated, trackPwl]);

  return (
    <FormWrapper>
      <FormElement hidden>
        <Input
          id="signUpInputHiddenEmail"
          data-testid="signUpInputHiddenEmail"
          type="email"
          name="email"
          label="Email"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          fullWidth
          autoComplete="username"
        />
      </FormElement>
      <FormElement>
        <Input
          id="signUpInputName"
          data-testid="signUpInputName"
          type="text"
          name="name"
          label="Name"
          value={name}
          helpText={showNameError ? formErrors.name : ''}
          error={showNameError}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="name"
        />
      </FormElement>
      <FormElement>
        <Input
          id="signUpInputMobile"
          data-testid="signUpInputMobile"
          type="tel"
          maxLength={8}
          name="mobile"
          label="Mobile number"
          value={mobile}
          helpText={showMobileError ? formErrors.mobile : ''}
          error={showMobileError}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="tel-national"
          className="SSOPage__input-number-reset"
        />
      </FormElement>
      <FormElement>
        <Input
          id="signUpInputPassword"
          data-testid="signUpInputPassword"
          type="password"
          name="password"
          label="Password"
          value={password}
          helpText={passWordHelpText}
          error={showPasswordError}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="new-password"
        />
      </FormElement>
      <FormElement spacing={{ mt: 3, mb: 2.5 }}>
        <ConsentCheckbox
          data-testid="signUpCheckboxOptIn"
          name="marketingConsentPreference"
          label={<GroupConsent />}
          checked={marketingConsentPreference}
          onChange={handleChange}
        />
      </FormElement>
      <FormActions spacing={{ mb: 3 }}>
        <ButtonContained
          data-testid="signUpButtonSubmit"
          fullWidth
          compact
          disabled={isSubmitButtonDisabled}
          loading={isSubmitting}
          onClick={submitForm}
        >
          Submit
        </ButtonContained>
      </FormActions>
      <FormFooter>
        <TnC type="signup" />
      </FormFooter>
    </FormWrapper>
  );
};

const EmailSignUp = () => {
  const { signUp, userContext, trackPwl } = useAuth0();
  const { goPrev } = useFlowControl();
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Landed',
      event_label: 'SignUp_Email',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userEmail = userContext.email || '';

  const initialValues = {
    email: userEmail,
    name: '',
    mobile: '',
    password: '',
    marketingConsentPreference: true,
  };

  const validate = (values) => {
    const schema = YupObject().shape({
      name: InputValidator.nameValidation(SCHEMA.name),
      mobile: InputValidator.contactNumberValidation(SCHEMA.mobile),
      password: InputValidator.newPasswordValidation(SCHEMA.password).label('Password'),
    });

    let errors = {};

    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      e.errors = { password: '' };
      e.inner.forEach((elmError) => {
        if (elmError.path === 'password') {
          e.errors.password += elmError.errors[0];
        } else {
          [e.errors[elmError.path]] = elmError.errors;
        }
      });
      errors = e;
    }

    return errors;
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Click',
      event_label: 'SignUp_Email_Submit',
    });

    setError(null);
    const apiPromise = new Promise((resolve, reject) => {
      signUp(
        {
          ...values,
        },
        (err) => {
          setSubmitting(false);
          if (err) {
            reject(err);
          }
          resolve();
        },
      );
    });

    try {
      await apiPromise;

      resetForm();
    } catch (e) {
      setError(e);
      Logger.error(e);

      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Message',
        event_label: 'SignUp_Email_Fail',
      });
    }
  };

  return (
    <>
      <BackButtonDesktop action={goPrev} />
      <PageContent>
        <PageHeader header="Create an account" backAction={goPrev} />
        {userEmail && <UserTag content={userEmail} data-testid="tag-userEmail" />}
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
          validate={validate}
        >
          {(formikProps) => <FormContent {...formikProps} />}
        </Formik>
      </PageContent>
    </>
  );
};

export default EmailSignUp;
